import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Grid,
  Input,
  List,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";

import personaApi from "../persona/persona.api";
import passcodesApi from "./passcodes.api";

const { Search } = Input;
const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const PasscodeAssignments = () => {
  const [passcodes, setPasscodes] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [selectedCompanions, setSelectedCompanions] = useState({});
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingPasscode, setSavingPasscode] = useState("");
  const [savingAnalyticsPasscode, setSavingAnalyticsPasscode] = useState("");

  const [briefingModalOpen, setBriefingModalOpen] = useState(false);
  const [briefingPasscode, setBriefingPasscode] = useState("");
  const [userName, setUserName] = useState("");
  const [baselineBriefing, setBaselineBriefing] = useState("");
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingSaving, setBriefingSaving] = useState(false);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const personaOptions = useMemo(() => {
    return personas
      .slice()
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
      .map((persona) => ({
        value: persona._id,
        label: persona.name,
      }));
  }, [personas]);

  const filteredPasscodes = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    const rows = !search
      ? passcodes
      : passcodes.filter((row) => {
          return [
            row.passcode,
            row.assignedCompanionName1,
            row.assignedPersonaId1,
            row.assignedCompanionName2,
            row.assignedPersonaId2,
            row.userName,
            row.claimedBy,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(search));
        });

    return rows.slice().sort((a, b) => {
      const aTime = Date.parse(String(a.lastSeenAt || "")) || 0;
      const bTime = Date.parse(String(b.lastSeenAt || "")) || 0;

      if (aTime !== bTime) {
        return bTime - aTime;
      }

      return String(a.passcode || "").localeCompare(String(b.passcode || ""));
    });
  }, [passcodes, searchText]);

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const compareText = (a, b, fieldName) => {
    return String(a[fieldName] || "").localeCompare(String(b[fieldName] || ""));
  };

  const compareBoolean = (a, b, fieldName) => {
    return Number(Boolean(a[fieldName])) - Number(Boolean(b[fieldName]));
  };

  const compareDate = (a, b, fieldName) => {
    const aTime = a[fieldName] ? new Date(a[fieldName]).getTime() : 0;
    const bTime = b[fieldName] ? new Date(b[fieldName]).getTime() : 0;

    return aTime - bTime;
  };

  const getAssignmentState = (record) => {
    const selected = selectedCompanions[record.passcode] || {};
    const selectedPersonaId1 = selected.assignedPersonaId1 || "";
    const selectedPersonaId2 = selected.assignedPersonaId2 || "";

    return {
      selectedPersonaId1,
      selectedPersonaId2,
      unchanged:
        selectedPersonaId1 === (record.assignedPersonaId1 || "") &&
        selectedPersonaId2 === (record.assignedPersonaId2 || ""),
      duplicate:
        Boolean(selectedPersonaId1) &&
        Boolean(selectedPersonaId2) &&
        selectedPersonaId1 === selectedPersonaId2,
    };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const [passcodeResponse, personaResponse] = await Promise.all([
        passcodesApi.getPasscodes(),
        personaApi.getPersonas(),
      ]);

      const passcodeRows = passcodeResponse.data?.passcodes || [];
      const personaRows = personaResponse.data || [];

      const nextSelectedCompanions = {};

      passcodeRows.forEach((row) => {
        nextSelectedCompanions[row.passcode] = {
          assignedPersonaId1: row.assignedPersonaId1 || undefined,
          assignedPersonaId2: row.assignedPersonaId2 || undefined,
        };
      });

      setPasscodes(passcodeRows);
      setPersonas(personaRows);
      setSelectedCompanions(nextSelectedCompanions);
    } catch (error) {
      console.error("Error loading passcode assignments:", error);
      message.error("Failed to load passcode assignments.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCompanionChange = (passcode, fieldName, assignedPersonaId) => {
    setSelectedCompanions((current) => ({
      ...current,
      [passcode]: {
        ...(current[passcode] || {}),
        [fieldName]: assignedPersonaId,
      },
    }));
  };

  const handleSave = async (record) => {
    const selected = selectedCompanions[record.passcode] || {};
    const assignedPersonaId1 = selected.assignedPersonaId1 || "";
    const assignedPersonaId2 = selected.assignedPersonaId2 || "";

    if (!assignedPersonaId1) {
      message.warning("Please choose companion 1 first.");
      return;
    }

    if (assignedPersonaId2 && assignedPersonaId2 === assignedPersonaId1) {
      message.warning("Please choose two different companions.");
      return;
    }

    setSavingPasscode(record.passcode);

    try {
      const response = await passcodesApi.assignCompanion(
        record.passcode,
        assignedPersonaId1,
        assignedPersonaId2
      );

      const companionName1 = response.data.assignedCompanionName1;
      const companionName2 = response.data.assignedCompanionName2;

      message.success(
        companionName2
          ? `Assigned ${companionName1} and ${companionName2} to ${record.passcode}.`
          : `Assigned ${companionName1} to ${record.passcode}.`
      );

      await fetchData();
    } catch (error) {
      console.error("Error saving companion assignment:", error);
      message.error("Failed to save companion assignment.");
    } finally {
      setSavingPasscode("");
    }
  };

  const handleEditBriefing = async (record) => {
    setBriefingPasscode(record.passcode);
    setUserName("");
    setBaselineBriefing("");
    setBriefingModalOpen(true);
    setBriefingLoading(true);

    try {
      const response = await passcodesApi.getBaselineBriefing(record.passcode);

      setUserName(response.data?.userName || "");
      setBaselineBriefing(response.data?.baselineBriefing || "");
    } catch (error) {
      console.error("Error loading baseline briefing:", error);
      message.error("Failed to load baseline briefing.");
      setBriefingModalOpen(false);
    } finally {
      setBriefingLoading(false);
    }
  };

  const handleSaveBriefing = async () => {
    if (!briefingPasscode) {
      return;
    }

    setBriefingSaving(true);

    try {
      await passcodesApi.updateBaselineBriefing(
        briefingPasscode,
        userName,
        baselineBriefing
      );

      message.success(
        userName.trim() || baselineBriefing.trim()
          ? "Baseline briefing saved."
          : "Baseline briefing cleared."
      );

      setBriefingModalOpen(false);
      setBriefingPasscode("");
      setUserName("");
      setBaselineBriefing("");

      await fetchData();
    } catch (error) {
      console.error("Error saving baseline briefing:", error);
      message.error("Failed to save baseline briefing.");
    } finally {
      setBriefingSaving(false);
    }
  };

  const handleAnalyticsChange = async (record, enabled) => {
    setSavingAnalyticsPasscode(record.passcode);

    try {
      await passcodesApi.updateAnalyticsEnabled(record.passcode, enabled);

      setPasscodes((current) =>
        current.map((row) =>
          row.passcode === record.passcode
            ? { ...row, analyticsEnabled: enabled }
            : row
        )
      );

      message.success(
        enabled ? "Analytics enabled." : "Analytics disabled."
      );
    } catch (error) {
      console.error("Error updating analytics setting:", error);
      message.error("Failed to update analytics setting.");
    } finally {
      setSavingAnalyticsPasscode("");
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: "Passcode",
      dataIndex: "passcode",
      key: "passcode",
      sorter: (a, b) => compareText(a, b, "passcode"),
      render: (value) => <Text strong>{value}</Text>,
      fixed: "left",
      width: 130,
    },
    {
      title: "Claimed",
      dataIndex: "isClaimed",
      key: "isClaimed",
      sorter: (a, b) => compareBoolean(a, b, "isClaimed"),
      render: (value) =>
        value ? (
          <Tag color="green">Claimed</Tag>
        ) : (
          <Tag>Not claimed</Tag>
        ),
      width: 120,
    },
    {
      title: "Primary companion",
      key: "chooseCompanion1",
      width: 220,
      render: (_, record) => (
        <Select
          showSearch
          placeholder="Choose companion 1"
          style={{ width: "100%" }}
          value={
            selectedCompanions[record.passcode]?.assignedPersonaId1 ||
            undefined
          }
          optionFilterProp="label"
          options={personaOptions}
          onChange={(value) =>
            handleCompanionChange(record.passcode, "assignedPersonaId1", value)
          }
        />
      ),
    },
    {
      title: "Secondary companion",
      key: "chooseCompanion2",
      width: 220,
      render: (_, record) => (
        <Select
          allowClear
          showSearch
          placeholder="Optional companion 2"
          style={{ width: "100%" }}
          value={
            selectedCompanions[record.passcode]?.assignedPersonaId2 ||
            undefined
          }
          optionFilterProp="label"
          options={personaOptions}
          onChange={(value) =>
            handleCompanionChange(
              record.passcode,
              "assignedPersonaId2",
              value || undefined
            )
          }
        />
      ),
    },
    {
      title: "Baseline briefing",
      key: "baselineBriefing",
      width: 190,
      render: (_, record) => (
        <Space>
          {record.hasBaselineBriefing ? (
            <Tag color="green">Set</Tag>
          ) : (
            <Tag>Not set</Tag>
          )}

          <Button onClick={() => handleEditBriefing(record)}>
            {record.hasBaselineBriefing ? "Edit" : "Add"}
          </Button>
        </Space>
      ),
    },
    {
      title: "Enable analytics",
      key: "analyticsEnabled",
      width: 160,
      render: (_, record) => (
        <Switch
          checked={Boolean(record.analyticsEnabled)}
          loading={savingAnalyticsPasscode === record.passcode}
          disabled={savingAnalyticsPasscode === record.passcode}
          onChange={(enabled) => handleAnalyticsChange(record, enabled)}
          aria-label={`Enable analytics for ${record.passcode}`}
        />
      ),
    },
    {
      title: "Last seen",
      dataIndex: "lastSeenAt",
      key: "lastSeenAt",
      sorter: (a, b) => compareDate(a, b, "lastSeenAt"),
      defaultSortOrder: "descend",
      render: formatDate,
      width: 180,
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      sorter: (a, b) => compareDate(a, b, "expiresAt"),
      render: formatDate,
      width: 180,
    },
    {
      title: "Action",
      key: "action",
      width: 180,
      fixed: "right",
      render: (_, record) => {
        const { selectedPersonaId1, unchanged, duplicate } =
          getAssignmentState(record);

        return (
          <Button
            type="primary"
            disabled={!selectedPersonaId1 || unchanged || duplicate}
            loading={savingPasscode === record.passcode}
            onClick={() => handleSave(record)}
          >
            Save assignment
          </Button>
        );
      },
    },
  ];

  const renderMobilePasscode = (record) => {
    const { selectedPersonaId1, unchanged, duplicate } =
      getAssignmentState(record);

    return (
      <List.Item key={record.passcode}>
        <Card className="passcode-mobile-card">
          <div className="passcode-card-header">
            <div>
              <Text type="secondary">Passcode</Text>
              <Title level={4} className="passcode-card-code">
                {record.passcode}
              </Title>
            </div>

            {record.isClaimed ? (
              <Tag color="green">Claimed</Tag>
            ) : (
              <Tag>Not claimed</Tag>
            )}
          </div>

          <div className="passcode-card-field">
            <span className="passcode-card-field-label">
              Primary companion
            </span>
            <Select
              showSearch
              placeholder="Choose primary companion"
              style={{ width: "100%" }}
              value={
                selectedCompanions[record.passcode]?.assignedPersonaId1 ||
                undefined
              }
              optionFilterProp="label"
              options={personaOptions}
              onChange={(value) =>
                handleCompanionChange(
                  record.passcode,
                  "assignedPersonaId1",
                  value
                )
              }
            />
          </div>

          <div className="passcode-card-field">
            <span className="passcode-card-field-label">
              Secondary companion
            </span>
            <Select
              allowClear
              showSearch
              placeholder="Optional secondary companion"
              style={{ width: "100%" }}
              value={
                selectedCompanions[record.passcode]?.assignedPersonaId2 ||
                undefined
              }
              optionFilterProp="label"
              options={personaOptions}
              onChange={(value) =>
                handleCompanionChange(
                  record.passcode,
                  "assignedPersonaId2",
                  value || undefined
                )
              }
            />
          </div>

          <div className="passcode-card-briefing">
            <div className="passcode-card-briefing-title">
              <Text strong>Baseline briefing</Text>
              {record.hasBaselineBriefing ? (
                <Tag color="green">Set</Tag>
              ) : (
                <Tag>Not set</Tag>
              )}
            </div>

            <Button onClick={() => handleEditBriefing(record)}>
              {record.hasBaselineBriefing ? "Edit" : "Add"}
            </Button>
          </div>

          <div className="passcode-card-field">
            <span className="passcode-card-field-label">
              Enable analytics
            </span>
            <Switch
              checked={Boolean(record.analyticsEnabled)}
              loading={savingAnalyticsPasscode === record.passcode}
              disabled={savingAnalyticsPasscode === record.passcode}
              onChange={(enabled) => handleAnalyticsChange(record, enabled)}
              aria-label={`Enable analytics for ${record.passcode}`}
            />
          </div>

          <div className="passcode-card-meta">
            <div>
              <span className="passcode-meta-label">Last seen</span>
              <span className="passcode-meta-value">
                {formatDate(record.lastSeenAt)}
              </span>
            </div>

            <div>
              <span className="passcode-meta-label">Expires</span>
              <span className="passcode-meta-value">
                {formatDate(record.expiresAt)}
              </span>
            </div>
          </div>

          <Button
            block
            type="primary"
            disabled={!selectedPersonaId1 || unchanged || duplicate}
            loading={savingPasscode === record.passcode}
            onClick={() => handleSave(record)}
          >
            Save assignment
          </Button>
        </Card>
      </List.Item>
    );
  };

  return (
    <div className="page-shell">
      <div className="page-heading-row">
        <div>
          <Title level={1}>Passcode Assignments</Title>
        </div>
      </div>

      <div className="page-toolbar">
        <Search
          allowClear
          enterButton="Search"
          placeholder="Search passcodes, user names or companions"
          className="page-search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onSearch={(value) => setSearchText(value)}
        />

        <Button onClick={fetchData} disabled={loading}>
          Refresh
        </Button>
      </div>

      {isMobile ? (
        <List
          className="passcode-mobile-list"
          loading={loading}
          dataSource={filteredPasscodes}
          pagination={
            filteredPasscodes.length > 12
              ? {
                  pageSize: 12,
                  size: "small",
                  showSizeChanger: false,
                }
              : false
          }
          renderItem={renderMobilePasscode}
        />
      ) : (
        <Table
          dataSource={filteredPasscodes}
          columns={columns}
          loading={loading}
          rowKey="passcode"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 1580 }}
        />
      )}

      <Modal
        title="Baseline briefing"
        open={briefingModalOpen}
        onOk={handleSaveBriefing}
        onCancel={() => {
          setBriefingModalOpen(false);
          setBriefingPasscode("");
          setUserName("");
          setBaselineBriefing("");
        }}
        okText="Save briefing"
        confirmLoading={briefingSaving}
        okButtonProps={{ disabled: briefingLoading }}
        width="min(620px, calc(100vw - 24px))"
        className="briefing-modal"
      >
        <Paragraph>
          Keep this short and stable. Record only information the user wants
          their Comfort Companion to know.
        </Paragraph>

        <Paragraph strong style={{ marginBottom: 6 }}>
          User's name
        </Paragraph>

        <Input
          maxLength={100}
          value={userName}
          disabled={briefingLoading}
          placeholder="Example: Pat"
          onChange={(event) => setUserName(event.target.value)}
          style={{ marginBottom: 20 }}
        />

        <Paragraph strong style={{ marginBottom: 6 }}>
          Additional baseline details
        </Paragraph>

        <Input.TextArea
          rows={6}
          maxLength={1500}
          showCount
          value={baselineBriefing}
          disabled={briefingLoading}
          placeholder="Example: Poppy is Pat's dog."
          onChange={(event) => setBaselineBriefing(event.target.value)}
        />
      </Modal>
    </div>
  );
};

export default PasscodeAssignments;
